import { forwardRef } from 'react';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Company } from '@gokrew/models/company';
import { TranslationNS } from '@gokrew/models/constants';
import styles from './CompanySwitcher.module.css';
import { companyName } from './companyLabels';
import { getColorForText } from '@/lib/utils';
import { getInitialsFromName } from '@/utility/utility';
import { AVATAR_COLORS } from '@/constants/avatar-colors';

const MAX_STACKED_AVATARS = 2;

interface SwitcherCardProps {
  companies: Company[];
  selected?: Company;
  isCollapsed: boolean;
  multiCompany: boolean;
  shortcutHint: string;
  className?: string;
  flat?: boolean;
  onOpen: () => void;
  hasLogo: (company: Company) => boolean;
  onLogoError: (companyId: string) => void;
}

/** The sidebar trigger — the collapsed mark, or the expanded workspace card. */
export const SwitcherCard = forwardRef<HTMLDivElement, SwitcherCardProps>(
  (
    {
      companies,
      selected,
      isCollapsed,
      multiCompany,
      shortcutHint,
      className,
      flat,
      onOpen,
      hasLogo,
      onLogoError,
    },
    ref
  ) => {
    const { t } = useTranslation(TranslationNS.LibsCommon);

    const stackedCompanies = companies
      .filter((company) => company.id !== selected?.id)
      .slice(0, MAX_STACKED_AVATARS);

    if (isCollapsed) {
      return (
        <div ref={ref} className={`${styles.collapsedWrap} ${className ?? ''}`}>
          <button
            type="button"
            className={styles.collapsedTile}
            onClick={onOpen}
            disabled={!multiCompany}
            title={
              multiCompany
                ? t('company_switcher.switch_workspace_shortcut', {
                    shortcut: shortcutHint,
                  })
                : selected && companyName(selected)
            }
            aria-label={
              (selected && companyName(selected)) ||
              t('company_switcher.switch')
            }
          >
            {/* Company logos never fit this tile nicely; the name (shown in
                the tooltip) identifies the workspace. */}
            <Building2 size={18} aria-hidden="true" />
          </button>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={`${styles.card} ${flat ? styles.cardFlat : ''} ${className ?? ''}`}
      >
        <button
          type="button"
          className={`${styles.cardTop} ${flat ? styles.cardTopFlat : ''}`}
          onClick={onOpen}
          disabled={!multiCompany}
          aria-label={t('company_switcher.switch')}
        >
          <span className={styles.cardText}>
            <span className={styles.cardName}>
              {selected ? companyName(selected) : ''}
            </span>
          </span>
        </button>

        {multiCompany && (
          <button
            type="button"
            className={styles.switchRow}
            onClick={onOpen}
            aria-label={t('company_switcher.switch_workspace_shortcut', {
              shortcut: shortcutHint,
            })}
          >
            <span className={styles.avatarStack} aria-hidden="true">
              {stackedCompanies.map((company) => (
                <span
                  key={company.id}
                  className={styles.stackedAvatar}
                  style={
                    hasLogo(company)
                      ? undefined
                      : {
                          background: getColorForText(
                            companyName(company),
                            AVATAR_COLORS
                          ),
                        }
                  }
                >
                  {hasLogo(company) ? (
                    <img
                      src={company.logo ?? ''}
                      alt=""
                      className={styles.avImg}
                      onError={() => onLogoError(company.id)}
                    />
                  ) : (
                    getInitialsFromName(companyName(company))
                  )}
                </span>
              ))}
            </span>

            <span className={styles.switchLabel}>
              {t('company_switcher.switch_workspace')}
            </span>
          </button>
        )}
      </div>
    );
  }
);

SwitcherCard.displayName = 'SwitcherCard';
